import {Box, Card, CardActionArea, CardMedia, CircularProgress, Fade, Stack, Typography,} from '@mui/material';
import PlotOptionsBar from '../globalComponents/PlotOptionsBar';
import {Warning} from '@mui/icons-material';
import {BASEURL} from "../../utils/api";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTheme} from "@mui/material/styles";


const DEFAULT_MESSAGES = Object.freeze([
  { text: 'Computationally intensive plot, please wait ...', delay: 5000 },
]);

const messagesMappingByTool = Object.freeze({
  'euv': Object.freeze([
    { text: 'Parsing input parameters ...', delay: 0 },
    { text: 'Running Fortran routines ...', delay: 750 },
    { text: 'Generating SunPy map ...', delay: 10000 },
    { text: 'Rendering plot ...', delay: 800 },
    DEFAULT_MESSAGES[0]
  ]),
  'euv_comparison': Object.freeze([
    { text: 'Parsing input parameters ...', delay: 0 },
    { text: 'Fetching data from Helioviewer ...', delay: 750 },
    { text: 'Running Fortran routines ...', delay: 2000 },
    { text: 'Generating SunPy map ...', delay: 10000 },
    { text: 'Rendering plot ...', delay: 750 },
    DEFAULT_MESSAGES[0]
  ]),
  'white_light': Object.freeze([
    { text: 'Parsing input parameters ...', delay: 0 },
    { text: 'Running Fortran routines ...', delay: 750 },
    { text: 'Generating SunPy map ...', delay: 10000 },
    { text: 'Rendering plot ...', delay: 750 },
    DEFAULT_MESSAGES[0]
  ]),
  'coronagraphs': Object.freeze([
    { text: 'Parsing input parameters ...', delay: 0 },
    { text: 'Fetching data from Helioviewer ...', delay: 750 },
    { text: 'Running Fortran routines ...', delay: 2000 },
    { text: 'Generating SunPy map ...', delay: 10000 },
    { text: 'Rendering plot ...', delay: 750 },
    DEFAULT_MESSAGES[0]
  ]),
  'time_series': Object.freeze([
    { text: 'Fetching spacecraft ephemeris ...', delay: 0 },
    { text: 'Interpolating MAS run data ...', delay: 750 },
    { text: 'Rendering plot ...', delay: 7500 },
    DEFAULT_MESSAGES[0]
  ]),
  'spacecraft_mapping': Object.freeze([
    { text: 'Fetching spacecraft ephemeris ...', delay: 0 },
    { text: 'Computing ballistic mapping ...', delay: 750 },
    { text: 'Tracing field lines ...', delay: 750 },
    { text: 'Rendering plot ...', delay: 1000 },
    DEFAULT_MESSAGES[0]
  ]),
});

function LoadingOverlay({ active, messages, textColor = 'red' }) {
  const [idx, setIdx] = useState(-1);
  const timers = useRef([]);

  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setIdx(-1);

    if (!active || !messages.length) return;

    // schedule messages using cumulative delays
    let t = 0;
    messages.forEach((m, i) => {
      t += m.delay;
      const id = window.setTimeout(() => setIdx(i), t);
      timers.current.push(id);
    });

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [active, messages]);

  if (!active) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5,
      }}
    >
      <Stack spacing={1} alignItems="center">
        <CircularProgress size={64} thickness={2} />
        <Box height={10}>
          <Fade in={idx >= 0} timeout={300} key={idx} unmountOnExit>
            <Typography sx={{ color: textColor, textAlign: 'center', textShadow: '1px 1px 2px rgba(0,0,0,0.2)' }}>
              {messages[idx]?.text}
            </Typography>
          </Fade>
        </Box>
      </Stack>
    </Box>
  );
}


const BaseMedia = ({meta, loading, validationError, cacheTrigger, ...props}) => {
  const theme = useTheme();
  const [showSlowNote, setShowSlowNote] = useState(false);

  useEffect(() => {
    if (!loading) {              // reset when loading finishes
      setShowSlowNote(false);
      return;
    }
    const id = setTimeout(() => setShowSlowNote(true), 100);
    return () => clearTimeout(id);
  }, [loading]);

  return (
      <Stack direction={'column'} sx={{backgroundColor: 'base.rothko7'}}>
        <PlotOptionsBar meta={meta} loading={loading} error={validationError} {...props}/>
        <Card
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200, // Used for equal aspect ratio plots
            zIndex:0,
            boxShadow: 'inset 5px 5px 4px rgba(0,0,0,0.3), inset -2px -2px 2px rgba(0,0,0,0.2)',
            borderRadius: '8px',
            p: 1,
            m: 1,
            position: 'relative',
            overflow: 'hidden'
          }}>

            <CardActionArea
              component='a'
              href={`${BASEURL + meta?.image}` ?? ''}
              target='_blank'
              rel='noopener noreferrer'
              disabled={validationError}
            >
              {!validationError && meta?.image && (
                <CardMedia
                  component='img'
                  src={`${BASEURL + meta?.image}?cache=${cacheTrigger}` ?? ''}
                  alt='PlotImage'
                  style={{ opacity: loading || validationError ? 0.4 : 1 }}
              />
              )}
            </CardActionArea>

            {loading && (
              <LoadingOverlay
                active={loading}
                messages={messagesMappingByTool[props.tool] ?? DEFAULT_MESSAGES}
              />
            )}

          {validationError && !loading && (
            <Stack
              alignItems={'center'}
              position={'absolute'}
              maxWidth={'180px'}
              textAlign={'center'}
            >
              <Warning color={'error'} sx={{fontSize: '60px'}}/>
              <Typography color={'error'} fontSize={12}>
                VALIDATION ERROR
              </Typography>
              <Typography color={'error'} fontSize={10} fontStyle={'italic'}>
                Check widgets for more information on how to resolve these conflicts
              </Typography>
            </Stack>
          )}
        </Card>
      </Stack>
    // </Box>
  );
};

export default BaseMedia;